import React from 'react';
import { ButtonProps, ButtonVariant } from '../../../types/DesignSystem/Button';
import Badge from '../Badge/Badge';

const variantStyles: Record<ButtonVariant, { default: string; hover: string }> = {
  primary: {
    default: 'tw-bg-blue-900 tw-text-white hover:tw-bg-blue-500',
    hover: 'tw-bg-blue-500 tw-text-white',
  },
  secondary: {
    default: 'tw-bg-white tw-text-blue-900 tw-border tw-border-blue-900 hover:tw-bg-blue-100 hover:tw-border-blue-500',
    hover: 'tw-bg-blue-100 tw-text-blue-900 tw-border tw-border-blue-500',
  },
  tertiary: {
    default: 'tw-bg-white tw-text-blue-900 hover:tw-bg-blue-100',
    hover: 'tw-bg-blue-100 tw-text-blue-900',
  },
};

const disabledStyle = 'tw-bg-grey-200 tw-text-grey-600 tw-cursor-not-allowed';

const getBadgeVariant = (buttonVariant: ButtonVariant, disabled?: boolean) => {
  if (disabled) return 'disabled';
  if (buttonVariant === 'primary') return 'warning';
  if (buttonVariant === 'secondary') return 'secondary';
  return 'secondary';
};

const iconSizeClasses: Record<string, string> = {
  small: 'tw-w-4 tw-h-4',
  medium: 'tw-w-5 tw-h-5',
  large: 'tw-w-6 tw-h-6',
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  disabled = false,
  showIcon = true,
  showBadge = false,
  icon = null,
  iconSize = 'medium',
  badgeContent,
  children,
  onClick,
  className = '',
  type = 'button',
}) => {
  const baseStyle =
    'tw-flex tw-items-center tw-justify-center tw-gap-1 tw-h-8 tw-px-4 tw-py-2 tw-rounded-sm tw-text-title tw-font-medium tw-transition-colors';

  const variantStyle = disabled ? disabledStyle : variantStyles[variant].default;

  const finalClassName = `${baseStyle} ${variantStyle} ${className}`;

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={finalClassName}
    >
      {showIcon && icon && <div className={`${iconSizeClasses[iconSize]} tw-flex tw-items-center tw-justify-center`}>{icon}</div>}
      <span>{children}</span>
      {showBadge && badgeContent !== undefined && (
        <Badge variant={getBadgeVariant(variant, disabled)}>{badgeContent}</Badge>
      )}
    </button>
  );
};

export default Button;
